/**
 * @fileoverview Maker-Checker workflow utilities.
 * Provides functions for filtering, validating, and managing change requests
 * in the maker-checker approval workflow for bot content modifications.
 * @module helperMethods/makerChecker/index
 */

import _ from "lodash";

/**
 * Represents a change request in the maker-checker workflow.
 */
interface Change {
  RowKey: string;
  PartitionKey: string;
  date_created: Date;
  last_modified: Date;
  modified_by: string;
  approved_by: string | null;
  status: string;
  type: string;
  mainType: string;
  department: string[];
  old_values: { [key: string]: string | boolean } | any;
  new_values: { [key: string]: string | boolean } | any;

  displayStatus?: "Deleted" | "Added" | "Modified";
}

/**
 * Filters changes to only include those belonging to the user's departments.
 * Validates change types and checks department membership for access control.
 *
 * @param {Change[]} changesList - Array of change requests to filter
 * @param {string[]} departments - User's department memberships
 * @returns {Change[]} Filtered array of changes accessible to the user
 *
 * @example
 * const userChanges = getDepartmentVerifiedChanges(allChanges, ['sales', 'marketing']);
 * // Returns only changes for sales, marketing, or general departments
 */
export function getDepartmentVerifiedChanges(
  changesList: Change[],
  departments: string[]
): Change[] {
  if (!Array.isArray(changesList)) {
    return []; // not valid
  }

  const validAndDepartmentVerifiedChanges = changesList.filter((change) => {
    const isValidate = validateWatsonChangeMainType(change);
    if (isValidate) {
      const changeDepartment: string | string[] = change.department;
      const changeDepartmentExists = _.get(change, "department.length", 0) > 0;
      const deptExist = departments && changeDepartmentExists;

      if (deptExist) {
        const changeBelongsToUserDept = checkChangeBelongsToUserDepartment(
          changeDepartment,
          departments
        );
        return changeBelongsToUserDept;
      } else {
        return true;
      }
    } else {
      return false;
    }
  });

  return validAndDepartmentVerifiedChanges;
}

/**
 * Validates that a change is of a supported Watson/bot type.
 *
 * @private
 * @param {Change} change - Change request to validate
 * @returns {boolean} True if mainType is entity, intent, dialog, or editor
 */
function validateWatsonChangeMainType(change: Change): boolean {
  return ["entity", "intent", "dialog", "editor", "trigger", "api"].includes(change.mainType); // valid for Watson
}

/**
 * Filters changes by status (pending or completed).
 *
 * @param {Change[]} changeList - Array of changes to filter
 * @param {string} selectedStatus - Status to filter by ('pending' or other)
 * @returns {Change[]} Filtered changes matching the status
 *
 * @example
 * const pending = validateChangeStatusList(changes, 'pending');
 * // Returns changes with status: add, modify, or delete
 */
export function validateChangeStatusList(changeList: Change[], selectedStatus: string): Change[] {
  return _.filter(changeList, (change) => {
    return validateChangeStatus(change, selectedStatus);
  });
}

/**
 * Validates if a change matches the selected status filter.
 *
 * @private
 * @param {Change} change - Change request to validate
 * @param {string} selectedStatus - Status to check against
 * @returns {boolean} True if change matches the status criteria
 */
function validateChangeStatus(change: Change, selectedStatus: string): boolean {
  return selectedStatus === "pending"
    ? ["add", "modify", "delete"].includes(change.status)
    : ["approve", "reject"].includes(change.status);
}

/**
 * Returns unique changes based on mainType or mainType+lastModified combination.
 * Deduplicates the change list for display purposes.
 *
 * @param {Change[]} changeList - Array of changes to deduplicate
 * @param {string} selectedStatus - Status context for deduplication strategy
 * @returns {Change[]} Array of unique changes
 *
 * @example
 * const unique = getUniqueChangeStatusList(changes, 'pending');
 * // Returns one change per mainType for pending, or per mainType+timestamp for completed
 */
export function getUniqueChangeStatusList(changeList: Change[], selectedStatus: string): Change[] {
  return _.uniqBy(changeList, (change) => {
    return selectedStatus === "pending"
      ? change.mainType
      : [change.mainType, change.last_modified].join();
  });
}

/**
 * Checks if a change belongs to one of the user's departments.
 * Changes with 'general' department are accessible to all users.
 *
 * @private
 * @param {string | string[]} changeDepartment - Department(s) associated with the change
 * @param {string[]} departments - User's department memberships
 * @returns {boolean} True if user has access to the change
 */
function checkChangeBelongsToUserDepartment(
  changeDepartment: string | string[],
  departments: string[]
): boolean {
  const lowerCasedChangeDept = parseChangeDepartment(changeDepartment);

  const belongsToUser = _.intersection(departments, lowerCasedChangeDept).length > 0;
  const isGeneral = lowerCasedChangeDept.includes("general");

  return belongsToUser || isGeneral;
}

/**
 * Parses change department data into a normalized lowercase string array.
 * Handles various input formats including comma-separated strings and arrays.
 *
 * @private
 * @param {string | string[]} changeDepartment - Department data to parse
 * @returns {string[]} Array of lowercase department names
 */
function parseChangeDepartment(changeDepartment: string | string[]): string[] {
  const changeDepartmentIsStringWithComma = isFirstElementStringifiedWithComma(changeDepartment);
  let changeDepartmentArr: string[] = [];
  if (changeDepartmentIsStringWithComma) changeDepartmentArr = changeDepartment[0].split(",");
  else if (typeof changeDepartment === "string") {
    changeDepartmentArr = [changeDepartment];
  } else {
    changeDepartmentArr = changeDepartment;
  }

  return _.chain(changeDepartmentArr)
    .flattenDeep()
    .compact()
    .map((v) => v && v.toLowerCase())
    .value();
}

/**
 * Checks if the first element of an array is a comma-separated string.
 * Used to detect stringified array formats in change data.
 *
 * @private
 * @param {string[] | any} array - Array to check
 * @returns {boolean} True if first element is a string containing commas
 */
function isFirstElementStringifiedWithComma(array: string[] | any): boolean {
  const firstElementExist = Array.isArray(array) && array[0];
  const firstElementIsString =
    typeof firstElementExist === "string" && firstElementExist.includes(",");
  return firstElementIsString;
}
